import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useBOAuth } from '../../context/BOAuthContext';
import { boAPI } from '../../lib/api';
import { toast } from 'sonner';
import { BOLayout } from './BOLayout';
import { Plus, X, FileText, Trash2, Search, Pencil } from 'lucide-react';
import { NairaInput } from '../../lib/nairaInput';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';

const formatNaira = (amount) => new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(amount || 0);
const methodLabels = { cash: 'Cash', bank_transfer: 'Bank Transfer', cheque: 'Cheque', pos: 'POS', online: 'Online' };

const emptyForm = {
  client_id: '',
  client_search: '',
  plot_ids: [],
  amount: '',
  fee_date: new Date().toISOString().split('T')[0],
  purpose: '',
  payment_method: 'bank_transfer',
  bank_reference: '',
  notes: '',
};

const BODevelopmentFees = () => {
  const navigate = useNavigate();
  const { role } = useBOAuth();
  const isReadOnly = role === 'accountant';

  const [fees, setFees] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [submitting, setSubmitting] = useState(false);
  const [confirm, setConfirm] = useState({ open: false, fee: null });
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);

  const openEdit = (f) => {
    setEditRow(f);
    setEditForm({
      amount: f.amount || 0,
      fee_date: f.fee_date || '',
      purpose: f.purpose || '',
      payment_method: f.payment_method || 'bank_transfer',
      bank_reference: f.bank_reference || '',
      notes: f.notes || '',
    });
  };
  const submitEdit = async () => {
    if (!editRow) return;
    setEditSaving(true);
    try {
      await boAPI.updateDevelopmentFee(editRow.id, { ...editForm, amount: parseFloat(editForm.amount) || 0 });
      toast.success('Development fee updated');
      setEditRow(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setEditSaving(false); }
  };
  const formRef = useRef(null);

  useEffect(() => {
    if (showForm && formRef.current) {
      setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    }
  }, [showForm]);

  const load = async () => {
    try {
      const [feesRes, clientsRes] = await Promise.all([
        boAPI.getDevelopmentFees(),
        boAPI.getClients(),
      ]);
      setFees(feesRes.data.fees || []);
      setTotalAmount(feesRes.data.total_amount || 0);
      // Only show clients that actually have plots assigned
      setClients((clientsRes.data.clients || []).filter(c => (c.assigned_plot_ids?.length || c.assigned_plot_id)));
    } catch (e) { toast.error('Failed to load development fees'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // Find selected client + their plots
  const selectedClient = useMemo(() => clients.find(c => c.id === form.client_id), [clients, form.client_id]);
  const clientPlots = useMemo(() => {
    if (!selectedClient) return [];
    const plot_ids = selectedClient.assigned_plot_ids?.length ? selectedClient.assigned_plot_ids : (selectedClient.assigned_plot_id ? [selectedClient.assigned_plot_id] : []);
    return plot_ids.map(pid => ({ id: pid, label: selectedClient.plot_info?.id === pid ? `${selectedClient.plot_info.plot_number} · ${selectedClient.plot_info.estate_name}` : pid }));
  }, [selectedClient]);

  const filteredClients = useMemo(() => {
    const q = (form.client_search || '').toLowerCase().trim();
    if (!q) return clients.slice(0, 8);
    return clients.filter(c => (c.full_name || '').toLowerCase().includes(q) || (c.phone || '').includes(q)).slice(0, 8);
  }, [clients, form.client_search]);

  const togglePlot = (pid) => {
    setForm(f => ({ ...f, plot_ids: f.plot_ids.includes(pid) ? f.plot_ids.filter(x => x !== pid) : [...f.plot_ids, pid] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_id) { toast.error('Select a client'); return; }
    if (!form.plot_ids.length) { toast.error('Pick at least one plot to link this fee to'); return; }
    if (!form.amount || parseFloat(form.amount) <= 0) { toast.error('Amount required'); return; }
    setSubmitting(true);
    try {
      const payload = {
        client_id: form.client_id,
        plot_ids: form.plot_ids,
        amount: parseFloat(form.amount),
        fee_date: form.fee_date,
        purpose: form.purpose || null,
        payment_method: form.payment_method,
        bank_reference: form.bank_reference || null,
        notes: form.notes || null,
      };
      const res = await boAPI.createDevelopmentFee(payload);
      toast.success('Development fee recorded — opening receipt');
      setShowForm(false);
      setForm({ ...emptyForm });
      load();
      // Open receipt
      setTimeout(() => navigate(`/backoffice/development-fees/${res.data.fee.id}/receipt`), 400);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record');
    } finally { setSubmitting(false); }
  };

  if (loading) return <BOLayout><div className="flex items-center justify-center min-h-[400px]"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600" /></div></BOLayout>;

  return (
    <BOLayout>
      <div className="space-y-6" data-testid="bo-dev-fees-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-serif font-bold text-neutral-900">Development Fees</h1>
            <p className="text-neutral-500 mt-1">{fees.length} fee{fees.length !== 1 ? 's' : ''} recorded · Total {formatNaira(totalAmount)}</p>
          </div>
          {!isReadOnly && (
            <Button onClick={() => { setForm({ ...emptyForm }); setShowForm(true); }} className="rounded-xl bg-primary-900 hover:bg-primary-800" data-testid="add-dev-fee-btn">
              <Plus className="h-4 w-4 mr-2" /> Record Development Fee
            </Button>
          )}
        </div>

        {/* Form */}
        {showForm && (
          <Card ref={formRef} className="border-primary-200 shadow-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Record New Development Fee</CardTitle>
                <CardDescription>Linked to a client and their plot(s). A receipt is generated.</CardDescription>
              </div>
              <button onClick={() => setShowForm(false)} className="p-2 hover:bg-neutral-100 rounded-lg"><X className="h-5 w-5" /></button>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label>Client *</Label>
                  {form.client_id ? (
                    <div className="mt-1.5 flex items-center justify-between rounded-xl border border-green-300 bg-green-50 px-3 py-2">
                      <span className="text-sm"><strong>{selectedClient?.full_name}</strong> · {selectedClient?.phone}</span>
                      <button type="button" onClick={() => setForm(f => ({ ...f, client_id: '', plot_ids: [], client_search: '' }))} className="text-xs text-red-600">change</button>
                    </div>
                  ) : (
                    <>
                      <div className="relative mt-1.5">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
                        <Input value={form.client_search} onChange={(e) => setForm({ ...form, client_search: e.target.value })} placeholder="Search by name or phone..." className="pl-10 rounded-xl" data-testid="dev-fee-client-search" />
                      </div>
                      <div className="mt-2 max-h-48 overflow-y-auto rounded-xl border border-neutral-200 divide-y divide-neutral-200">
                        {filteredClients.length === 0 && <p className="p-3 text-xs text-neutral-500">No matching clients with assigned plots.</p>}
                        {filteredClients.map(c => (
                          <button type="button" key={c.id} onClick={() => setForm(f => ({ ...f, client_id: c.id, client_search: '' }))} className="w-full text-left px-3 py-2 hover:bg-primary-50" data-testid={`dev-fee-client-${c.id}`}>
                            <div className="font-medium text-sm">{c.full_name}</div>
                            <div className="text-xs text-neutral-500">{c.phone} · {c.plot_info ? `${c.plot_info.plot_number} (${c.plot_info.estate_name})` : 'no plots assigned'}</div>
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                {form.client_id && clientPlots.length > 0 && (
                  <div>
                    <Label>Plot(s) this fee applies to *</Label>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {clientPlots.map(p => {
                        const sel = form.plot_ids.includes(p.id);
                        return (
                          <button key={p.id} type="button" onClick={() => togglePlot(p.id)} className={`px-3 py-2 rounded-lg border text-xs font-medium transition-colors ${sel ? 'bg-primary-900 text-white border-primary-900' : 'bg-white text-neutral-700 border-neutral-200 hover:bg-primary-50'}`} data-testid={`dev-fee-plot-${p.id}`}>
                            {p.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div><Label>Amount (Naira) *</Label><NairaInput value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} className="mt-1.5 rounded-xl" data-testid="dev-fee-amount" /></div>
                  <div><Label>Date *</Label><Input type="date" value={form.fee_date} onChange={(e) => setForm({ ...form, fee_date: e.target.value })} className="mt-1.5 rounded-xl" /></div>
                  <div>
                    <Label>Payment Method</Label>
                    <select value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })} className="mt-1.5 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm">
                      <option value="cash">Cash</option><option value="bank_transfer">Bank Transfer</option><option value="cheque">Cheque</option><option value="pos">POS</option><option value="online">Online</option>
                    </select>
                  </div>
                  <div className="sm:col-span-2 lg:col-span-3"><Label>Purpose / What is the development?</Label><Input value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} placeholder="e.g., Road grading, fencing, perimeter survey" className="mt-1.5 rounded-xl" data-testid="dev-fee-purpose" /></div>
                  <div><Label>Bank reference</Label><Input value={form.bank_reference} onChange={(e) => setForm({ ...form, bank_reference: e.target.value })} placeholder="optional" className="mt-1.5 rounded-xl" /></div>
                  <div className="sm:col-span-2"><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="optional" className="mt-1.5 rounded-xl" /></div>
                </div>

                <div className="flex gap-3 justify-end pt-2">
                  <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="rounded-xl">Cancel</Button>
                  <Button type="submit" disabled={submitting} className="rounded-xl bg-primary-900 hover:bg-primary-800" data-testid="dev-fee-submit">{submitting ? 'Saving...' : 'Record & Generate Receipt'}</Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Table */}
        <Card className="border-neutral-border shadow-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="dev-fees-table">
              <thead>
                <tr className="border-b bg-neutral-50">
                  <th className="text-left px-4 py-3 font-medium text-neutral-600">Date</th>
                  <th className="text-left px-4 py-3 font-medium text-neutral-600">Client</th>
                  <th className="text-left px-4 py-3 font-medium text-neutral-600 hidden md:table-cell">Plots</th>
                  <th className="text-left px-4 py-3 font-medium text-neutral-600 hidden lg:table-cell">Purpose</th>
                  <th className="text-right px-4 py-3 font-medium text-neutral-600">Amount</th>
                  <th className="text-right px-4 py-3 font-medium text-neutral-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {fees.map(f => (
                  <tr key={f.id} className="border-b hover:bg-neutral-50 transition-colors" data-testid={`dev-fee-row-${f.id}`}>
                    <td className="px-4 py-3 text-neutral-600">{f.fee_date}</td>
                    <td className="px-4 py-3 font-medium">{f.client_name}</td>
                    <td className="px-4 py-3 hidden md:table-cell text-neutral-600">{(f.plots_info || []).map(p => p.plot_number).join(', ') || '--'}</td>
                    <td className="px-4 py-3 hidden lg:table-cell text-neutral-700 max-w-xs truncate" title={f.purpose}>{f.purpose || '--'}</td>
                    <td className="px-4 py-3 text-right font-bold text-green-700">{formatNaira(f.amount)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => navigate(`/backoffice/development-fees/${f.id}/receipt`)} className="p-2 rounded-lg hover:bg-neutral-100 text-primary-700" title="Receipt" data-testid={`dev-fee-receipt-${f.id}`}><FileText className="h-4 w-4" /></button>
                        {!isReadOnly && <button onClick={() => openEdit(f)} className="p-2 rounded-lg hover:bg-neutral-100 text-neutral-700" title="Edit" data-testid={`dev-fee-edit-${f.id}`}><Pencil className="h-4 w-4" /></button>}
                        {!isReadOnly && <button onClick={() => setConfirm({ open: true, fee: f })} className="p-2 rounded-lg hover:bg-red-50 text-red-600" title="Delete" data-testid={`dev-fee-delete-${f.id}`}><Trash2 className="h-4 w-4" /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
                {fees.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-neutral-500">No development fees recorded yet</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      <ConfirmDialog
        open={confirm.open}
        onOpenChange={(o) => !o && setConfirm({ open: false, fee: null })}
        title="Delete this development fee?"
        description={confirm.fee ? `${formatNaira(confirm.fee.amount)} from ${confirm.fee.client_name} will be permanently removed. The receipt will no longer be accessible.` : ''}
        confirmLabel="Delete"
        onConfirm={async () => {
          try {
            await boAPI.deleteDevelopmentFee(confirm.fee.id);
            toast.success('Development fee deleted');
            setConfirm({ open: false, fee: null });
            load();
          } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
        }}
      />

      <Dialog open={!!editRow} onOpenChange={(o) => !o && setEditRow(null)}>
        <DialogContent className="max-w-md" data-testid="edit-dev-fee-dialog">
          <DialogHeader><DialogTitle>Edit Development Fee</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Date</Label><Input type="date" value={editForm.fee_date || ''} onChange={(e) => setEditForm({ ...editForm, fee_date: e.target.value })} /></div>
            <div><Label>Amount (₦)</Label><Input type="number" step="0.01" value={editForm.amount ?? ''} onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })} data-testid="edit-dev-fee-amount" /></div>
            <div><Label>Purpose</Label><Input value={editForm.purpose || ''} onChange={(e) => setEditForm({ ...editForm, purpose: e.target.value })} /></div>
            <div>
              <Label>Method</Label>
              <select value={editForm.payment_method} onChange={(e) => setEditForm({ ...editForm, payment_method: e.target.value })} className="w-full border rounded-md h-10 px-3 text-sm">
                {Object.entries(methodLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div><Label>Bank Reference</Label><Input value={editForm.bank_reference || ''} onChange={(e) => setEditForm({ ...editForm, bank_reference: e.target.value })} /></div>
            <div><Label>Notes</Label><Input value={editForm.notes || ''} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditRow(null)}>Cancel</Button>
            <Button onClick={submitEdit} disabled={editSaving} data-testid="save-dev-fee-btn">{editSaving ? 'Saving...' : 'Save changes'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </BOLayout>
  );
};

export default BODevelopmentFees;
